import { CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "./index";
import { Modal } from "./Modal";

interface Toast {
  id: number;
  message: string;
  kind: "error" | "success";
}

interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

interface FeedbackCtx {
  toast: (message: string, kind?: Toast["kind"]) => void;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const Ctx = createContext<FeedbackCtx | null>(null);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const nextId = useRef(0);

  const toast = useCallback((message: string, kind: Toast["kind"] = "error") => {
    const id = ++nextId.current;
    setToasts((t) => [...t.slice(-3), { id, message, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 6000 : 3000);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolve) => setConfirmState({ ...opts, resolve })), []);

  useEffect(() => {
    const onError = (e: Event) => toast(errorMessage((e as CustomEvent).detail));
    window.addEventListener("app:error", onError);
    return () => window.removeEventListener("app:error", onError);
  }, [toast]);

  const settle = (v: boolean) => {
    confirmState?.resolve(v);
    setConfirmState(null);
  };

  return (
    <Ctx.Provider value={{ toast, confirm }}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-80 flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto flex items-start gap-2 rounded-lg bg-elevated p-3 text-sm shadow-pop">
            {t.kind === "error" ? (
              <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0 text-danger" />
            ) : (
              <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-[var(--dot-green)]" />
            )}
            <span className="flex-1">{t.message}</span>
            <button type="button" className="text-muted hover:text-fg" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}>
              <XMarkIcon className="size-4" />
            </button>
          </div>
        ))}
      </div>
      <Modal
        open={!!confirmState}
        onClose={() => settle(false)}
        size="sm"
        title={confirmState?.title}
        footer={
          <>
            <Button onClick={() => settle(false)}>Cancel</Button>
            <Button variant={confirmState?.danger ? "danger" : "primary"} onClick={() => settle(true)} autoFocus>
              {confirmState?.confirmLabel ?? "Confirm"}
            </Button>
          </>
        }
      >
        <div className={cn("text-sm text-muted")}>{confirmState?.message}</div>
      </Modal>
    </Ctx.Provider>
  );
}

export function useFeedback() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useFeedback outside FeedbackProvider");
  return ctx;
}
